import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { getToken } from '../lib/api';
import { alert, haptic } from '../lib/telegram';
import { fmtLongDay, fmtTime } from '../lib/time';
import { getLang, useT } from '../lib/i18n';
import { costsLaterText, type CostsLater } from './Coverage';

/**
 * «Убрать из занятия»: someone added to a pair or group by mistake comes
 * out again, as if never booked (server: booking/remove.ts). Credits
 * back, the cash charge dropped, nothing in their attendance; the others
 * go back to the price for one fewer.
 *
 * A message goes to them by default, neutral: the booking was a mistake
 * and nothing was charged. «Не сообщать» is for someone who never knew —
 * on by default when they were added under ten minutes ago.
 *
 * Used from the session card's ⋯ and from the notes sheet's player row.
 */

const BASE = import.meta.env.VITE_API_URL ?? '';
async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { ...(init.body == null ? {} : { 'content-type': 'application/json' }),
      authorization: `Bearer ${getToken()}`, ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error(b.error ?? `http_${res.status}`);
  }
  return res.json();
}

type T = ReturnType<typeof useT>;

/** What «Заменить» would move instead, once the session has started (server: seatState). */
export interface SeatMoves { ratings: boolean; notes: boolean; cash: number; credits: number }
export interface SeatState {
  blocked: 'rated' | 'paid' | null; started: boolean; moves: SeatMoves;
  /** What the others' top-up would leave uncovered ahead (N5). */
  costsLater?: CostsLater;
}

/**
 * Whether this seat can still come out: asked of the server, which alone
 * knows the rule — only this player's own saved rating, notes or cash
 * received block it; drafts and the others' never do.
 */
export const fetchSeatState = (bookingId: string) => api<SeatState>(`/api/coach/bookings/${bookingId}/seat-state`);

/** Why this person cannot be taken out any more, or null. */
export function removeRefusal(t: T, name: string, o: { rated: boolean; paid: boolean }): string | null {
  if (o.rated) return t('co.rm.rated', { name });
  if (o.paid) return t('co.rm.paid', { name });
  return null;
}

/** The server's refusal, in words. */
export function removeErrorText(t: T, name: string, code: string): string {
  if (code === 'rated') return t('co.rm.rated', { name });
  if (code === 'paid') return t('co.rm.paid', { name });
  if (code === 'last_participant') return t('co.rm.last', { name });
  return t('co.failed', { message: code });
}

/** Added under ten minutes ago: they probably never knew, so no message by default. */
export const addedJustNow = (bookedAt?: string | null) =>
  !!bookedAt && Date.now() - new Date(bookedAt).getTime() < 10 * 60000;

export function RemoveSheet({ bookingId, name, startsAt, inBot, bookedAt, onClose, onDone }: {
  bookingId: string; name: string; startsAt: string; inBot: boolean; bookedAt?: string | null;
  onClose: () => void; onDone: () => void;
}) {
  const t = useT();
  const recent = addedJustNow(bookedAt);
  const [silent, setSilent] = useState(recent);
  const [busy, setBusy] = useState(false);
  // The others may pay their larger share from credits now: what that
  // leaves uncovered ahead, said before «Убрать».
  const [later, setLater] = useState<string | null>(null);
  useEffect(() => {
    fetchSeatState(bookingId).then((s) => setLater(costsLaterText(t, s.costsLater))).catch(() => setLater(null));
  }, [bookingId]);

  async function go() {
    setBusy(true);
    try {
      const r = await api<{ refunded: number; notified: boolean }>(`/api/coach/bookings/${bookingId}/remove`, {
        method: 'POST', body: JSON.stringify({ notify: !silent }),
      });
      haptic.success();
      onDone();
      onClose();
      await alert(t('co.rm.done', { name })
        + (r.refunded > 0 ? t('co.rm.doneCredits', { n: new Intl.NumberFormat(getLang() === 'ru' ? 'ru-RU' : 'en-GB', { maximumFractionDigits: 2 }).format(r.refunded), credits: t.word('book.credit', r.refunded) }) : '')
        + (r.notified && inBot ? t('co.rm.doneTold') : t('co.rm.doneSilent')));
    } catch (err: any) {
      haptic.error();
      await alert(removeErrorText(t, name, err.message));
    } finally { setBusy(false); }
  }

  return createPortal(
    <div className="sheet" onClick={onClose}>
      <div className="sheet__panel" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__title">{t('co.rm.title', { name })}</div>
        <div className="card__when">{fmtLongDay(new Date(startsAt))}, {fmtTime(startsAt)}</div>
        <p className="rm__body">{t('co.rm.body')}</p>

        <div className="field">
          <span className="field__label field__label--strong">
            {t('co.rm.silent')}
            <small>{recent ? t('co.rp.recent', { name }) : t('co.rm.silentHint')}</small>
          </span>
          <button type="button" role="switch" aria-checked={silent} aria-label={t('co.rm.silent')}
            className="switch" onClick={() => { haptic.tap(); setSilent(!silent); }}>
            <span className="switch__knob" />
          </button>
        </div>
        <div className="card__when rm__say">
          {!inBot ? t('co.rm.noBot', { name }) : silent ? t('co.rm.wontSay', { name }) : t('co.rm.willSay', { name })}
        </div>
        {later && <div className="warnline">{later}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button className="paybar__opt" style={{ flex: 1 }} onClick={onClose}>{t('co.pkg.back')}</button>
          <button className="paybar__opt"
            style={{ flex: 2, background: 'var(--danger)', color: '#fff', borderColor: 'var(--danger)' }}
            disabled={busy} onClick={go}>
            {t('co.rm.do')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
